import { toast } from '@raystack/apsara';
import * as Sentry from '@sentry/browser';
import { createFormattedAreaEl } from '@studio/map';
import { bbox, centroid, simplify } from '@turf/turf';

import api from '@src/api';
import { setSatellite } from '@src/redux/_reducers/filtersReducer';
import { setBounds, setCenter } from '@src/redux/_reducers/mapControl';
import {
  setActiveAoi,
  setShowEditAoiImageList,
  setSplitMode
} from '@src/redux/_reducers/projectReducer';
import store from '@src/redux/store';

import {
  COLLECTION_MAP,
  CONSTELLATION_CONTROL
} from './constants/LENS_CONSTANTS';
import { MAP_AOI_SIZE_BUFFER } from './constants/MAP_OPERATIONS';
import { HYPER_SPEC_SOURCES } from './constants/MODE_INFERENCE_DEFAULTS';

/**
 *
 * @param {*} err - js error captured during file upload
 * @param {*} data - uploaded file contents
 * @param {*} file - file object (for file name)
 */
export async function SentryErrorHandler(err, fileName, data) {
  Sentry.withScope(scope => {
    scope.addAttachment({
      filename: fileName,
      data: data
    });

    Sentry.captureEvent({
      level: 'error',
      message: 'File upload failed!',
      extra: err
    });
  });
}

export const SimplifyPolygon = geojson => {
  if (!geojson || Object.keys(geojson).length === 0) {
    return geojson;
  }

  return simplify(geojson, { tolerance: 0.01, highQuality: false });
};

export function transformAoiMetaData(data, satellite_name) {
  if (!data || !data.bands || !data.indice_formulas) {
    return {};
  }

  const isHyperSpectral = HYPER_SPEC_SOURCES.includes(satellite_name) || false;
  const metadata = {
    bands: data.bands,
    indices: {},
    rescale: data.collections[0].rescale,
    preview_url: data.collections[0].preview_url,
    composite: { ...data?.rgb_composite },
    isHyperSpectral
  };
  const indicesList = Object.keys(data.indice_formulas);
  indicesList.forEach(ind => {
    if (data.indice_formulas[ind]) {
      metadata.indices[ind] = data.indice_formulas[ind];
    }
  });

  return metadata;
}

export async function getAoiMetaData(satellite_name) {
  let { data } = await api.imageSearch.get(`/satellites/${satellite_name}`);
  return transformAoiMetaData(data, satellite_name);
}

export async function FetchSatDetails(aoi) {
  try {
    const metadata = await getAoiMetaData(
      aoi.lens_satellite_id || aoi.satellite_name
    );
    //update active AOI from API response
    let obj = { ...aoi, metadata };

    store.dispatch(setActiveAoi(obj));
    store.dispatch(setSplitMode(false));
    store.dispatch(setShowEditAoiImageList(false));
  } catch (error) {
    toast.error('Oops! Something went wrong!', {
      description:
        'We are unable to fetch AOI details due a fatal issue in our systems. Please try again!'
    });
  }
}

// set map parameters
function setBoundsAndCenter(aoi) {
  let staCenter = centroid(aoi.geometry).geometry.coordinates;
  let stacBounds = bbox(aoi.geometry);
  store.dispatch(
    setBounds([
      [stacBounds[0], stacBounds[1]],
      [stacBounds[2], stacBounds[3]]
    ])
  );
  store.dispatch(setCenter([staCenter[1], staCenter[0]]));
}

export async function GetAOIDetails(selectedAOI) {
  try {
    setBoundsAndCenter(selectedAOI);
    FetchSatDetails(selectedAOI);
  } catch (error) {
    if (error?.response?.status == 400) {
      console.error(error.response.data);

      toast.error('Something went wrong!', {
        description: error.response.data.detail
      });
    } else if (error?.response?.status == 404) {
      store.dispatch(setActiveAoi({}));
      toast.error('Assets not found!', {
        description:
          "This AOI doesn't seem to have any assets. You can still view it without raster visualization!"
      });
      FetchSatDetails(selectedAOI);
    } else {
      toast.error('Oops!', {
        description:
          'Something went wrong while fetching AOI details! Please try again.'
      });
      console.error(error);
    }
  }
}

export function isStandardPlan(activePlan, trialPlan) {
  // user maybe on standard plan and trialing enterprise or professional.
  // In this case the limit will not be 200 sq km

  return !activePlan && !trialPlan;
}

export function getMaxArea(isStandardPlan) {
  // MAX AOI allowed for Standard Plan is 200 sq km
  // TODO: This function should be replaced by values sent from Frontier in the future

  return Number(
    isStandardPlan
      ? 200
      : (import.meta.env.VITE_AOI_AREA ?? MAP_AOI_SIZE_BUFFER)
  );
}

export function getMaxAreaMessage(maxArea, isStandardPlan) {
  return isStandardPlan ? (
    <>
      Standard Plan allows AOI size up to 200 km<sup>2</sup> only
    </>
  ) : (
    <>
      Maximum AOI size can be {createFormattedAreaEl(maxArea)} only. Please
      adjust the AOI.
    </>
  );
}

// export function maxAreaReachedError(maxArea, isStandardPlan) {
//   return toast.error('Area Limit Reached!', {
//     description: getMaxAreaMessage(maxArea, isStandardPlan),
//     action: (
//       <Button
//         size='small'
//         variant='outline'
//         color='neutral'
//         style={{ textWrap: 'nowrap' }}
//         data-test-id='btn-error-toast'
//         onClick={() =>
//           store.dispatch(setIAMDialogState({ route: '/plans', isOpen: true }))
//         }
//       >
//         Upgrade Plan
//       </Button>
//     )
//   });
// }

export const formatSatelliteInfo = constellation => {
  const bands = constellation.bands.map(b => b.name);
  const selectedCollection = CONSTELLATION_CONTROL.includes(constellation.id)
    ? constellation.collections.find(
        i => i.id === COLLECTION_MAP[`${constellation.id}`]
      )
    : constellation.collections[0];

  const satelliteObject = {
    name: constellation.name,
    provider: selectedCollection?.provider ?? '',
    queryables: selectedCollection?.queryables ?? [],
    satellite_id: constellation.id,
    collection_id: selectedCollection?.id ?? '',
    eo_bands: bands
  };

  return satelliteObject;
};

// Helper function to add a satellite
export const addSatellite = (constellation, filters, dispatch) => {
  const satelliteObject = formatSatelliteInfo(constellation);

  const updatedSatellites = [...filters.satellite, satelliteObject];
  dispatch(setSatellite(updatedSatellites));
};

export const addSatellites = (constellations = [], dispatch) => {
  const satelliteObjects = constellations.map(constellation => {
    const bands = constellation.bands.map(b => {
      return b.name;
    });

    const selectedCollection = CONSTELLATION_CONTROL.includes(constellation.id)
      ? constellation.collections.find(
          i => i.id === COLLECTION_MAP[constellation.id]
        )
      : constellation.collections[0];

    const satelliteObject = {
      name: constellation.name,
      provider: selectedCollection?.provider ?? '',
      queryables: selectedCollection?.queryables ?? [],
      satellite_id: constellation.id,
      collection_id: selectedCollection?.id ?? '',
      eo_bands: bands
    };
    return satelliteObject;
  });
  dispatch(setSatellite(satelliteObjects));
};

// Helper function to remove a satellite
const removeSatellite = (constellation, filters, dispatch) => {
  const updatedSatellites = filters.satellite.filter(
    i => i.satellite_id !== constellation.id
  );
  dispatch(setSatellite(updatedSatellites));
};

// Main onCheckedChange handler
export const handleSatelliteCheck = (
  check,
  constellation,
  filters,
  dispatch
) => {
  if (check) {
    addSatellite(constellation, filters, dispatch);
  } else {
    removeSatellite(constellation, filters, dispatch);
  }
};
